import { Box }                      from '@chakra-ui/react';
import DaisukiListRow               from './DaisukiListRow';
import DaisukiModel                 from '@/models/services/website/DaisukiModel';
import EventModel                   from '@/models/services/website/EventModel';
import HeartIcon                    from '@/assets/icons/HeartIcon';
import { Link }                     from 'react-router-dom';
import List                         from '@/components/list/List';
import ParticipationModel           from '@/models/services/website/ParticipationModel';
import PlaceModel                   from '@/models/services/website/PlaceModel';
import useApiCollection             from '@widesk-core/hooks/useApiCollection';
import usePagedCollection           from '@widesk-core/hooks/usePagedCollection';
import { useTranslation }           from 'react-i18next';
import WebsiteEntertainmentModel    from '@/models/services/website/WebsiteEntertainmentModel';
import StandPageModel               from '@/models/services/website/StandPageModel';

interface IDaisukiListBoxProps {
	websiteEntertainment: WebsiteEntertainmentModel;
}

const DaisukiListBox = (props: IDaisukiListBoxProps) => {
	const { websiteEntertainment } = props;

	const { t, i18n } = useTranslation();

	const daisukiCollection = usePagedCollection(DaisukiModel);
	const eventCollection = useApiCollection(EventModel);
	const participationCollection = useApiCollection(ParticipationModel);
	const standPageCollection = useApiCollection(StandPageModel);
	const placeCollection = useApiCollection(PlaceModel);

	const websiteLink = websiteEntertainment.website.url ? `${websiteEntertainment.website.url}/${i18n.language}/` : '';

	const contentIdsByType = (type: DaisukiContentType) =>
		daisukiCollection.models
			.filter(d => d.daisukiContent.type === type)
			.map(d => String(d.daisukiContent.contentId));
	
	return (
		<List
			afterFetch={async () => {
				await daisukiCollection.whenIsLoaded(d => d.daisukiContent);
				const eventIds = contentIdsByType('event');
				const participationIds = contentIdsByType('participation');
				const standIds = contentIdsByType('stand');
				const placeIds = contentIdsByType('place');				

				await Promise.all([
					eventIds.length         ? eventCollection.clear().listBy(eventIds, 'id')                 : Promise.resolve(),
					participationIds.length ? participationCollection.clear().listBy(participationIds, 'id') : Promise.resolve(),
					standIds.length         ? standPageCollection.clear().listBy(standIds, 'stand')          : Promise.resolve(),
					placeIds.length         ? placeCollection.clear().listBy(placeIds, 'id')                 : Promise.resolve(),
				]);

				await standPageCollection.whenIsLoaded(sp => sp.stand);
			}}
			beforeFetch={() => {daisukiCollection.setSort('updatedAt', 'desc')}}
			collection={daisukiCollection}
			renderRow={(model) => {
				const contentId = String(model.daisukiContent?.contentId);
				let resource: EventModel | ParticipationModel | StandPageModel | PlaceModel | null = null;
				switch (model.daisukiContent?.type) {
					case 'event':
						resource = eventCollection.getById(contentId);
						break;
					case 'participation':
						resource = participationCollection.getById(contentId);
						break;
					case 'stand':
						resource = standPageCollection.find(sp => String(sp.getId('stand')) === String(contentId));
						break;
					case 'place':
						resource = placeCollection.getById(contentId);
						break;
				
					default:
						resource = null;
						break;
				}
				return <DaisukiListRow
					daisuki={model}
					websiteEntertainment={websiteEntertainment}
					resource={resource}
				/>
			}}
			options={{
				clickable: true,
				emptyAlert: websiteLink ? {
					title: (
						<Box display="inline" width="100%">
							<span>{t('pages.daisuki.list.empty.alert.part1')} </span>
							<Link
								to={websiteLink}
								target="_blank"
								style={{ fontWeight: 'bold', textDecoration: 'underline', width: 'fit-content' }}
							>
								{t('pages.daisuki.list.empty.alert.link')}
							</Link>
							<span> {t('pages.daisuki.list.empty.alert.part2')}</span>
						</Box>
					),
				} : undefined,
				emptyMessages: {
					title: t('pages.daisuki.list.empty.title'),
					description: t('pages.daisuki.list.empty.description'),
					icon: <HeartIcon size="xl" />,
				},
				filters: {
					'daisukiContent.tags[jts:registry:service:website][website_entertainment]': websiteEntertainment.urn,
				},
				itemsMargin: '10px 0px 10px 0px',
				itemsPadding: '10px',
				padding: '0px 10px 0px 10px',
				withSeparators: true,
			}}
		/>
	)
}

export default DaisukiListBox;